import { useState } from 'react';
import type React from 'react';
import { FormInput, FormSelect, SlideModal } from '@podus/ui';
import { api } from '@/lib/api';

const ROLES = ['Case Manager', 'Mediator', 'Admin'] as const;

type View = 'form' | 'success';

interface AddUserModalProps {
    open: boolean;
    onClose: () => void;
}

export default function AddUserModal({ open, onClose }: AddUserModalProps) {
    const [view, setView] = useState<View>('form');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [role, setRole] = useState<string>(ROLES[0]);

    const handleClose = () => {
        setView('form');
        setName('');
        setEmail('');
        setPhone('');
        setRole(ROLES[0]);
        onClose();
    };

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            await api.post('/api/staff', { name, email, phone, role });
            setView('success');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add user.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <SlideModal open={open} onClose={handleClose} onBack={view === 'success' ? () => setView('form') : undefined}>
            {view === 'form' ? (
                <>
                    <div className="mx-6 mt-4 rounded-lg border border-[#eee] py-2.5 text-center text-[15px] font-bold text-secondary">
                        Add New Users
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="flex flex-1 flex-col px-6 pt-6">
                        <div className="space-y-5">
                            <FormInput
                                title="Name"
                                id="user-name"
                                placeholder="e.g  Adebayo Oluwaseun Johnson"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                variant="white"
                                required
                            />
                            <FormInput
                                title="Email"
                                id="user-email"
                                type="email"
                                placeholder="e.g  Adebayo Oluwaseun Johnson"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                variant="white"
                                required
                            />
                            <FormInput
                                title="Phone Number"
                                id="user-phone"
                                placeholder="e.g  Adebayo Oluwaseun Johnson"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                variant="white"
                                required
                            />

                            <FormSelect
                                id="user-role"
                                title="Role"
                                value={role}
                                onChange={setRole}
                                options={ROLES.map((r) => ({ label: r, value: r }))}
                            />
                        </div>

                        {/* Spacer + Submit */}
                        <div className="mt-auto pb-6 pt-8">
                            {error && <p className="mb-3 text-sm text-red-500">{error}</p>}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[15px] font-bold text-white transition-all hover:bg-secondary-400 disabled:opacity-60"
                            >
                                {isLoading ? 'Adding…' : 'Add New'}
                            </button>
                        </div>
                    </form>
                </>
            ) : (
                /* ── Success View ── */
                <div className="flex flex-1 flex-col px-8 pb-6 text-center">
                    <div className="flex flex-1 flex-col items-center justify-center">
                        {/* Green badge icon */}
                        <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                            <circle cx="40" cy="40" r="36" fill="#22c55e" />
                            <circle cx="40" cy="40" r="36" fill="url(#badge-grad)" />
                            {/* Outer petals */}
                            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                                <circle
                                    key={angle}
                                    cx={40 + 30 * Math.cos((angle * Math.PI) / 180)}
                                    cy={40 + 30 * Math.sin((angle * Math.PI) / 180)}
                                    r="10"
                                    fill="#22c55e"
                                />
                            ))}
                            {/* Inner circle */}
                            <circle cx="40" cy="40" r="24" fill="#22c55e" />
                            {/* Checkmark */}
                            <path
                                d="M28 40l8 8 16-16"
                                stroke="white"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                            />
                            <defs>
                                <radialGradient id="badge-grad" cx="0.5" cy="0.3" r="0.7">
                                    <stop offset="0%" stopColor="#4ade80" />
                                    <stop offset="100%" stopColor="#16a34a" />
                                </radialGradient>
                            </defs>
                        </svg>

                        <h2 className="mt-6 text-xl font-bold text-secondary">User Added Successfully!</h2>

                        <p className="mt-4 max-w-xs text-[15px] leading-relaxed text-[#888]">
                            The new internal user has been added and an invitation email has been sent to their
                            registered email address with login credentials.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={handleClose}
                        className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[15px] font-bold text-white transition-all hover:bg-secondary-400"
                    >
                        Close
                    </button>
                </div>
            )}
        </SlideModal>
    );
}
